import { useState, useEffect } from 'react';
import { useStore } from '../../store/pages/forms/store';
import useForm from '../../pages/AdminContentMgt/Components/useForm';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AddToMessageHistory,
  CreateMessages,
  GetPostWithTitle,
  GetSocialMediaFileList,
  PostMessageToLinkedin,
  PostXTweet,
  SearchPostKeywords,
} from '../../api/marketing';
import { GetProductNames } from '../../api/product';
import { useAsyncDebounce } from '../../hooks/util/useAsyncDebounce';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ImageList,
  ImageListItem,
  List,
  ListItem,
  TextField,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomModal from '../Global/CustomModal/CustomModal';
import { Dropdown, MultipleSelect } from '../Inputs/Dropdown/Dropdown';
import { GetAllCasgUsers } from '../../api/agencyadmin';
import { ImageTile } from '../Images/ImageTile/ImageTile';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/LinkedIn';
import './SocialMedia.scss';
import CustomTextField from '../Inputs/TextFields/CustomTextField/CustomTextField';
import LoadingIndicator from '../Global/loading-indicator/loading-indicator';
import { SOCIAL_NETWORK } from '../../pages/Marketing/Components/PostsList';

const emailRowStyle = {
  margin: '1em 2em',
};
const iconMap = {
  Facebook: <FacebookIcon />,
  Linkedln: <LinkedInIcon />,
  Twitter: <TwitterIcon />,
  Instagram: <InstagramIcon />,
};
const textLimit = { Facebook: 5000, Linkedln: 3000, X: 280, Instagram: 2200 };
export const CreatePostModal = ({
  show,
  setShow,
  categoryOptions,
  setStatus,
  setShowConfirmModal,
  confirmed,
  setConfirmed,
}) => {
  const [selectedCategory, setCategory] = useState(show?.categoryId);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState();
  const [keyword, setKeyword] = useState([]);
  const [keywordText, setKeywordText] = useState('');
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [productNames, setProductNames] = useState();
  const updateRequest = useStore((state) => state.UPDATE_REQUESTS);
  const setDraftMessages = useStore((state) => state.SET_DRAFT_MESSAGES);
  const draftMessages = useStore((state) => state.draftMessages);
  const [title, setTitle] = useState('');
  const [casgUsers, setCasgUsers] = useState([]);
  const [titleExists, setTitleExists] = useState(false);
  const [availableToAll, setAvailableToAll] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(true);
  const [totalCount, setTotalCount] = useState();
  const [imageList, setImageList] = useState({
    Facebook: [],
    Twitter: [],
    Linkedln: [],
    Instagram: [],
  });
  const [imageSelected, setImageSelect] = useState([]);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ('Message' in fieldValues)
      temp.Message = fieldValues.Message ? '' : 'Message is a required field.';
    if ('CategoryID' in fieldValues)
      temp.CategoryID =
        fieldValues.CategoryID == 0 || fieldValues.CategoryID
          ? ''
          : 'Category is a required field.';
    if ('Title' in fieldValues) temp.Title = fieldValues.Title ? '' : 'Title is a required field.';

    setErrors({
      ...temp,
    });

    if (fieldValues == values) return Object.values(temp).every((x) => x == '');
  };

  const { values, errors, setErrors, handleDataChange, resetForm } = useForm(
    {
      Message: '',
      Network: '',
      CategoryID: '',
      ProductID: '',
      Keywords: [],
      Title: '',
      Published: false,
      Users: 'ALL',
      UsersGuid: '',
      ImageID: {},
    },
    true,
    validate
  );

  const keywordsQuery = useQuery({
    queryKey: ['keywords', keyword],
    queryFn: async () => await SearchPostKeywords({ keyword })(),
    enabled: keyword && keyword?.length >= 3,
  });

  // Handle keywords query success
  useEffect(() => {
    if (keywordsQuery.isSuccess && keywordsQuery.data) {
      const ko = keywordsQuery.data?.map(({ keyword, categoryID }) => ({
        value: categoryID,
        label: keyword,
      }));
      if (ko) {
        setKeywordOptions([...keywordOptions, ...ko]);
      }
    }
  }, [keywordsQuery.isSuccess, keywordsQuery.data]);

  const socialMediaFilesQuery = useQuery({
    queryKey: ['GetSocialMediaFileList', page, selectedNetwork],
    queryFn: async () =>
      await GetSocialMediaFileList([
        { queryParam: 'page', queryVal: page },
        { queryParam: 'network', queryVal: selectedNetwork },
      ])(),
    enabled: next && !!selectedNetwork && imageList[selectedNetwork]?.length === 0,
  });

  // Handle social media files query success
  useEffect(() => {
    if (socialMediaFilesQuery.isSuccess && socialMediaFilesQuery.data) {
      const data = socialMediaFilesQuery.data;
      setNext(false);
      setTotalCount(data.totalCount);

      if (imageList[selectedNetwork].length === 0) {
        setTotalCount(0);
        if (page !== 1) {
          setPage(page - 1);
        }
      }

      imageList[selectedNetwork] = [...imageList[selectedNetwork], ...data.items];
      setImageList(imageList);
    }
  }, [socialMediaFilesQuery.isSuccess, socialMediaFilesQuery.data]);

  const titleQuery = useQuery({
    queryKey: ['title', title],
    queryFn: async () => await GetPostWithTitle({ title })(),
    enabled: title?.length >= 3,
  });

  // Handle title query success
  useEffect(() => {
    if (titleQuery.isSuccess && titleQuery.data) {
      setTitleExists(!!titleQuery.data.item);
      let temp = { ...errors };
      if (titleQuery.data.item) {
        temp.Title = 'Title Exists. Please check if the existing post will meet your need.';
      } else {
        temp.Title = undefined;
      }
      setErrors({
        ...temp,
      });
    }
  }, [titleQuery.isSuccess, titleQuery.data]);

  const productNamesQuery = useQuery({
    queryKey: ['getProductNames', 0],
    queryFn: () => GetProductNames()(),
  });

  // Handle product names query success
  useEffect(() => {
    if (productNamesQuery.isSuccess && productNamesQuery.data) {
      const rawProducts = productNamesQuery.data;
      const products = Array.isArray(rawProducts)
        ? rawProducts
        : typeof rawProducts === 'string'
          ? JSON.parse(rawProducts)
          : rawProducts;
      const pn = products.map(({ ProductId, Title }) => ({ value: ProductId, label: Title }));
      pn.push({ value: 'Other', label: 'Other' });
      pn.push({ value: 'N/A', label: 'N/A' });
      setProductNames(pn);
    }
  }, [productNamesQuery.isSuccess, productNamesQuery.data]);

  const casgUsersQuery = useQuery({
    queryKey: ['GetAllCasgUsers'],
    queryFn: () => GetAllCasgUsers()()(),
  });

  // Handle CASG users query success
  useEffect(() => {
    if (casgUsersQuery.isSuccess && casgUsersQuery.data) {
      const source = Array.isArray(casgUsersQuery.data)
        ? casgUsersQuery.data
        : typeof casgUsersQuery.data === 'string'
          ? JSON.parse(casgUsersQuery.data)
          : casgUsersQuery.data;
      const users = source
        .map((element) => ({
          ...element,
          label: element.displayName,
          value: element.id,
        }))
        .sort((a, b) =>
          b.displayName < a.displayName ? -1 : b.displayName > a.displayName ? 1 : 0
        );
      setCasgUsers(users);
    }
  }, [casgUsersQuery.isSuccess, casgUsersQuery.data]);

  useEffect(() => {
    if (confirmed) {
      if (availableToAll) {
        values.Users = 'ALL';
        values.UsersGuid = '';
      }
      const keys = Object.keys(values.ImageID);
      const PostsMedia = keys.map((i) => ({ ImageId: values.ImageID[i], Network: i }));
      submitForm.mutate({
        Post: values,
        PostsMedia,
        Keywords: values.Keywords,
        RequestID: show?.requestId,
      });
      setShowConfirmModal(false);
    }
  }, [confirmed]);

  useEffect(() => {
    if (values.Title) {
      handleTitleChange(values?.Title || '');
    }
  }, [values?.Title]);

  const handleSearchTextChange = useAsyncDebounce(
    (value) => {
      setKeyword(value);
    },
    [1000]
  );

  const handleUserTextChange = useAsyncDebounce(
    (value) => {
      // search for user
    },
    [1000]
  );

  const handleTitleChange = useAsyncDebounce(
    (value) => {
      setTitle(value);
    },
    [1000]
  );

  const reset = () => {
    resetForm({});
    setCategory(undefined);
    setSelectedProduct('');
    setSelectedNetwork('');
    setImageList({ Facebook: [], Twitter: [], Linkedln: [] });
    setCurrentPage(0);
    setConfirmed(false);
  };

  const handleClose = () => {
    setShow(null);
    reset();
  };

  const submitForm = useMutation({
    mutationFn: async (data) => {
      return await CreateMessages(data)();
    },
  });

  // Handle submit form success
  useEffect(() => {
    if (submitForm.isSuccess && submitForm.data) {
      updateRequest(submitForm.data.approvedRequest);
      draftMessages.unshift(submitForm.data.post);
      setDraftMessages(draftMessages);
      setShow(false);
      setStatus({
        show: true,
        status: 'success',
        successMsg: `Message created successfully`,
      });
      reset();
    }
  }, [submitForm.isSuccess, submitForm.data]);

  // Handle submit form error
  useEffect(() => {
    if (submitForm.isError) {
      setStatus({
        show: true,
        status: 'error',
        successMsg: `Unable to create message, please try later.`,
      });
    }
  }, [submitForm.isError]);

  const handleSubmit = () => {
    if (values.Keywords?.length === 0) {
      values.Keywords = show?.keywords || [];
    }
    if (!values.CategoryID) {
      values.CategoryID = show?.categoryID;
    }

    if (validate()) {
      setShowConfirmModal(true);
    }
  };

  const nextPage = () => {
    if (validate()) {
      setCurrentPage(currentPage + 1);
    }
  };

  const page1 = (
    <div>
      {show?.requestId && (
        <>
          <div className="row" style={emailRowStyle}>
            <div className="col">Request ID: {show.requestId}</div>
          </div>
          <div className="row" style={emailRowStyle}>
            <div className="col">Request Description: {show.description}</div>
          </div>
        </>
      )}
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <Dropdown
            setValue={(category) => {
              setCategory(category);
              handleDataChange('CategoryID', category.value);
            }}
            selected={selectedCategory}
            value={show?.categoryID}
            error={errors.CategoryID}
            id="category-select-label"
            label="Category*"
            options={categoryOptions}
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <MultipleSelect
            setValue={(v) => {
              handleDataChange('Network', v.join());
            }}
            value={values.Network}
            id="network-select-label"
            label="Social Channel"
            options={SOCIAL_NETWORK}
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <Dropdown
            setValue={(product) => {
              setSelectedProduct(product);
              handleDataChange('ProductID', product.value);
            }}
            selected={selectedProduct}
            error={errors.ProductID}
            id="product-select-label"
            label="Product"
            options={productNames}
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <TextField
            fullWidth={true}
            variant="outlined"
            id="outlined-multiline-static"
            label="Title*"
            value={values.Title}
            error={errors.Title}
            helperText={errors.Title}
            onChange={(e) => {
              handleDataChange('Title', e.target.value);
            }}
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <TextField
            fullWidth={true}
            variant="outlined"
            id="outlined-multiline-static"
            label="Post Text*"
            multiline
            minRows={7}
            value={values.Message}
            error={errors.Message}
            helperText={errors.Message}
            onChange={(e) => {
              handleDataChange('Message', e.target.value);
            }}
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <Autocomplete
            freeSolo
            multiple
            defaultValue={show?.keywords || []}
            options={keywordOptions || []}
            getOptionLabel={(o) => o.label || o}
            onChange={(e, v) => {
              setSelectedKeywords(v);
              handleDataChange('Keywords', v);
            }}
            value={selectedKeywords}
            inputValue={keywordText}
            onInputChange={(event, newInputValue) => {
              setKeywordText(newInputValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                return (
                  <Chip
                    variant="outlined"
                    label={option.label || option}
                    {...getTagProps({ index })}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                onKeyDown={(event, v) => {
                  if (event.code === 'Comma' && keywordText.trim() !== '') {
                    event.preventDefault();

                    const ks = [...selectedKeywords, keywordText.trim()];
                    setSelectedKeywords(ks);
                    handleDataChange('Keywords', ks);
                    setKeywordText('');
                  }
                }}
                variant="outlined"
                {...params}
                onChange={(e) => {
                  handleSearchTextChange(e.target.value);
                }}
                label="Keywords"
              />
            )}
            id="tags-filled"
          />
        </div>
      </div>
      <div className="row" style={emailRowStyle}>
        <div className="col">
          <Box ml={2}>
            <Tooltip title="Available to All" placement="top" arrow>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={availableToAll}
                    onClick={(e) => {
                      setAvailableToAll(e.target.checked);
                    }}
                    className="select-item"
                  />
                }
                label="Available to All"
              />
            </Tooltip>
          </Box>
          {!availableToAll && (
            <Autocomplete
              freeSolo
              multiple
              options={casgUsers || []}
              getOptionLabel={(o) => o.label || o}
              onChange={(e, v) => {
                // Users is the legacy int-bracket audience list (bli-native picks have no legacy
                // id and are dropped here); UsersGuid is the GUID-bracket list — always present for
                // every selected agent, bli-native or migrated. Preferred going forward per the
                // dual-read design already in place on the read side (SocialsPostsController.GetMessages).
                const users = v
                  .map((item) => item.id)
                  .filter((id) => id !== null && id !== undefined)
                  .join(',');
                const usersGuid = v
                  .map((item) => item.userId)
                  .filter((id) => id !== null && id !== undefined)
                  .join(',');
                handleDataChange('Users', users);
                handleDataChange('UsersGuid', usersGuid);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  return (
                    <Chip
                      variant="outlined"
                      label={option.label || option}
                      {...getTagProps({ index })}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  variant="outlined"
                  {...params}
                  onChange={(e) => {
                    handleUserTextChange(e.target.value);
                  }}
                  label="Users"
                />
              )}
              id="tags-filled"
            />
          )}
        </div>
      </div>
    </div>
  );

  const page2 = (
    <div>
      <div className="image-table-container">
        <table className="vertical-table">
          <tr className="vertical-table-tab-container">
            <List className="link-text-container">
              {values?.Network?.split(',')?.map((n) => {
                return (
                  <ListItem
                    key={n}
                    id="link-text"
                    className={`${selectedNetwork === n ? 'opened' : ''}${imageSelected.includes(n) ? ' selected' : ' notselected'}`}
                    onClick={() => {
                      setSelectedNetwork(n);
                      if (Object.keys({}).length === 0 || !values.ImageID[selectedNetwork]) {
                        setNext(true);
                      }
                    }}
                  >
                    <p id={`link-text-${n}`}>{n}</p>
                  </ListItem>
                );
              })}
            </List>
          </tr>
          <td>
            {selectedNetwork && imageList[selectedNetwork] && (
              <>
                <List className="image-list-container">
                  {imageList[selectedNetwork]?.map(({ id, name, url }) => {
                    const isSelected = values.ImageID[selectedNetwork] === id;

                    return (
                      <ImageTile
                        key={`${name}${id}`}
                        id={id}
                        title={name}
                        imageUrl={url}
                        value={values.ImageID}
                        isSelected={isSelected}
                        handleCheckboxChange={() => {
                          setImageSelect([...imageSelected, selectedNetwork]);
                          handleDataChange('ImageID', { ...values.ImageID, [selectedNetwork]: id });
                        }}
                      />
                    );
                  })}
                </List>
              </>
            )}
          </td>
        </table>
        {selectedNetwork && !imageList[selectedNetwork] && (
          <div>No Images have been uploaded for {selectedNetwork}</div>
        )}
        {!selectedNetwork && (
          <div>Please select a social channel and then select and image for that channel</div>
        )}
      </div>
      {imageList[selectedNetwork]?.length != 0 &&
        totalCount > imageList[selectedNetwork]?.length &&
        !!selectedNetwork && (
          <Box
            display="block"
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              onClick={() => {
                setPage(page + 1);
                setNext(true);
              }}
            >
              More Images
            </Button>
          </Box>
        )}
    </div>
  );

  const body = {
    0: page1,
    1: page2,
  };

  return (
    <Dialog
      open={!!show}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      PaperProps={{ sx: { maxWidth: 800, width: '100%' } }}
    >
      <DialogTitle>
        Create New Social Media Post
        <IconButton onClick={handleClose} style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent id="email-form-modal-body" sx={{ maxHeight: 800, overflowY: 'auto' }}>
        {body[currentPage]}
      </DialogContent>
      <DialogActions>
        <button className="btn" type="button" onClick={handleClose}>
          Cancel
        </button>
        {currentPage === 0 && (
          <button className="btn btn-success" type="button" onClick={() => nextPage()}>
            Attach Image
          </button>
        )}
        {currentPage !== 0 && (
          <button
            className="btn btn-success"
            type="button"
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
        )}
        <button className="btn btn-danger" type="button" onClick={handleSubmit}>
          Submit
        </button>
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmationModal = ({ showConfirmed, setConfirmed, setShowConfirmed, message }) => (
  <CustomModal
    show={showConfirmed}
    onHide={() => setShowConfirmed(false)}
    title="Complete Confirmation"
    body={<>{message}</>}
    buttonOne={
      <button
        className="btn btn-primary"
        type="button"
        onClick={() => {
          setConfirmed(true);
        }}
      >
        Confirm
      </button>
    }
    buttonTwo={
      <button className="btn btn-default" type="button" onClick={() => setShowConfirmed(false)}>
        Close
      </button>
    }
    buttonThree={null}
  />
);

export const ImageListModal = ({ show, setShow, title, images }) => (
  <Dialog
    open={show}
    onClose={(_event, reason) => {
      if (reason !== 'backdropClick') setShow(false);
    }}
  >
    <DialogTitle>
      {title}
      <IconButton onClick={() => setShow(false)} style={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <ImageList sx={{ width: 500, height: 450 }} cols={3} rowHeight={164}>
        {images.map((item) => (
          <ImageListItem key={`${item.name}${item.id}`}>
            <img
              srcSet={`${item.url}?w=164&h=164&fit=crop&auto=format&dpr=2 2x`}
              src={`${item.url}?w=164&h=164&fit=crop&auto=format`}
              alt={item.title}
              loading="lazy"
            />
          </ImageListItem>
        ))}
      </ImageList>
    </DialogContent>
  </Dialog>
);

export const SocialPostModal = ({ show, setShow, setStatus }) => {
  const [showConfirmed, setShowConfirmed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setLoading] = useState(false);

  const selectedNetwork = show?.selectedNetwork;
  const message = show?.message;
  const isError = Object.keys(errors).length > 0;
  const isImageSelected = !!show?.selectedImageUrl;

  const addToHistory = useMutation({
    mutationFn: async (data) => {
      return await AddToMessageHistory(data)();
    },
  });

  if (confirmed && !isError) {
    setLoading(true);
    const { postsMedia, selectedNetwork } = show;
    if (postsMedia?.length > 0) {
      const selectedImageObject = postsMedia.filter((pm) => pm.network === selectedNetwork);
      if (selectedImageObject?.length > 0) {
        show.selectedImageUrl = selectedImageObject[0].image.url;
        show.selectedImageId = selectedImageObject[0].imageId;
      }
    }

    const onSuccess = () => {
      addToHistory.mutate({
        message,
        postID: show?.id,
        imageId: show?.selectedImageId,
        network: show?.selectedNetwork,
        status: 'Message Posted Successfully',
      });
      setConfirmed(false);
      setShowConfirmed(false);
      setStatus({
        show: true,
        status: 'success',
        successMsg: `Message successfully posted to ${selectedNetwork}`,
      });
      setShow(null);
      setLoading(false);
    };

    const onError = (error) => {
      setStatus({
        show: true,
        status: 'error',
        successMsg: `Unable to create message, please try later.`,
      });

      addToHistory.mutate({
        message,
        postID: show?.id,
        imageId: show?.selectedImageId,
        network: show?.selectedNetwork,
        status: `Unable to post message - ${JSON.stringify(error)}`,
      });
    };
    postMessageDictionary[selectedNetwork](show, onSuccess, onError);
    setConfirmed(false);
  }

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <Dialog
      open={!!show}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') setShow(null);
      }}
    >
      <DialogTitle>
        Post to {selectedNetwork}
        <IconButton
          onClick={() => setShow(null)}
          style={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        style={{ paddingRight: '30px', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Dropdown
          setValue={(page) => {
            setShow({ ...show, selectedPage: page });
          }}
          selected={show?.selectedPage || (show?.pageOptions && show?.pageOptions[0])}
          error=""
          id="page-select-label"
          label="Page"
          options={show?.pageOptions || []}
        />

        <CustomTextField
          label="Message"
          multiline={true}
          value={message || ''}
          errors={errors}
          setErrors={setErrors}
          onChange={(message) => {
            setShow({ ...show, message });
          }}
          maxLength={textLimit[selectedNetwork]}
        />

        <div className="image-list-container">
          {(show?.postsMedia?.length && show.network !== 'X') > 0 &&
            show?.postsMedia?.map((imageData) => {
              const { id, name, image } = imageData;

              return (
                <ImageTile
                  key={`${name}${id}`}
                  id={show.selectedImageId}
                  title={name}
                  imageUrl={image.url}
                  value={{ [selectedNetwork]: id }}
                  handleCheckboxChange={() => {
                    setShow({ ...show, selectedImageId: id, selectedImageUrl: image.url });
                  }}
                />
              );
            })}
        </div>
      </DialogContent>
      <DialogActions>
        {!showConfirmed && (
          <>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                if (!isError) {
                  setShowConfirmed(true);
                }
              }}
            >
              Post
            </button>
          </>
        )}
        {showConfirmed && (
          <>
            <div>Are you sure you want to share this post on your chosen social media site?</div>
            <Button
              className="btn btn-success"
              variant="outlined"
              onClick={() => setConfirmed(true)}
            >
              Yes
            </Button>
            <Button
              className="btn btn-danger"
              variant="outlined"
              onClick={() => setShowConfirmed(false)}
            >
              NO
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

const postMessageToFB = (
  { selectedPage, pageOptions, message, selectedImageUrl },
  onSuccess,
  onError
) => {
  const { pageId: defaultPageid, accessToken: defaultAccessToken } = pageOptions[0];
  let page = defaultPageid;
  let token = defaultAccessToken;
  if (selectedPage) {
    const { pageId, accessToken } = selectedPage;
    page = pageId;
    token = accessToken;
  }

  let path = '/feed';
  let url = '';
  let request = { message, access_token: token };

  if (selectedImageUrl) {
    path = '/photos';
    url = `${process.env.REACT_APP_FILE_REPOSITORY_BASE_URL}/SocialMedia${selectedImageUrl}`;
    request = { ...request, url };
  }

  FB.api(`/${page}${path}`, 'POST', request, (response) => {
    if (!response || response.error) {
      console.log('Error posting to facebook');
      onError(response.error);
    } else {
      console.log('Post ID:', response.id);
      onSuccess();
    }
  });
};

const postMessageToX = async (
  { id, pageOptions, message, selectedImageUrl },
  onSuccess,
  onError
) => {
  const { pageId, accessToken } = pageOptions[0];
  const t = await PostXTweet({
    postID: id,
    pageId,
    accessToken,
    message,
    imageUrl: selectedImageUrl,
  })();
  if (t?.data?.id) {
    onSuccess();
  } else {
    onError();
  }
};

const postMessageToIG = (
  { selectedPage, pageOptions, message, selectedImageUrl },
  onSuccess,
  onError
) => {
  const { pageId: defaultPageid, accessToken: defaultAccessToken } = pageOptions[0];
  let page = defaultPageid;
  let token = defaultAccessToken;
  if (selectedPage) {
    const { pageId, accessToken } = selectedPage;
    page = pageId;
    token = accessToken;
  }

  FB.api(
    `/${page}/media`,
    'POST',
    {
      access_token: token,
      caption: message,
      image_url: selectedImageUrl,
    },
    function (response) {
      if (!response || response.error) {
        console.log('Error posting to facebook');
        onError();
      } else {
        console.log('Post ID:', response.id);
        onSuccess();
      }
    }
  );
};

const postMessageToLinkedin = (
  { selectedPage, defaultPageId, pageOptions, message, selectedImageUrl },
  onSuccess,
  onError
) => {
  let { pageId, accessToken } = defaultPageId;
  if (selectedPage) {
    pageId = selectedPage.pageId;
    accessToken = selectedPage.accessToken;
  }

  const body = {
    author: pageId || defaultPageId,
    commentary: message,
    visibility: 'PUBLIC',
    distribution: {
      feedDistribution: 'MAIN_FEED',
      targetEntities: [],
      thirdPartyDistributionChannels: [],
    },
    lifecycleState: 'PUBLISHED',
    isReshareDisabledByAuthor: false,
  };

  const t = PostMessageToLinkedin(body, accessToken, [
    { queryParam: 'imageUrl', queryVal: selectedImageUrl },
  ])();
  if (t) {
    onSuccess();
  }
};

const postMessageDictionary = {
  Facebook: postMessageToFB,
  Instagram: postMessageToIG,
  X: postMessageToX,
  Linkedln: postMessageToLinkedin,
};
